import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef, useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
  useLazyDataModel,
  extend,
  params,
  shape,
  string,
  number,
  arrayOf,
  customType,
  bool
} from '@thd-nucleus/data-sources';
import {
  Card,
  CardBody,
  Typography,
  Badge,
  Button,
  TextField,
  Popover
} from '@one-thd/sui-atomic-components';
import { ErrorFilled, InfoFilled } from '@one-thd/sui-icons';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import THDStorageUtils, { SESSION_STORAGE } from '@thd-olt-global/thd-storage-utils';
import { TapToSpeak } from '../tap-to-speak/TapToSpeak';
import { AddToContext } from '../../AddToContext';
import { SCREENS, TRACK } from '../../AddToConstants';
import { formatMaterials, triggerAnalytics, normalizePageType } from '../../AddToHelpers';
import { useProductsBatched } from '../../hooks/useProductsBatched';

const getMaterialProduct = (materialItem, itemIdsMap) => {
  if (materialItem.name) {
    return { product: materialItem.name, brand: materialItem.brand };
  }
  const pIdata = itemIdsMap[materialItem.itemId];
  return { product: pIdata?.name || '', brand: pIdata?.brand || '' };
};

/**
 * AI List Builder component for describing projects and generating lists
 */
export const AIListBuilder = ({
  name,
  materials,
  hideTitle,
  refType,
  refId,
  onGenerate,
  title,
  description,
}) => {
  const TIMEOUT = 59000; // 59 seconds
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const { customerType } = useThdCustomer() || {};
  const enableLBAIQuestionerAgent = useConfigService('fs:isLBAIQuestionerAgentEnabled');
  const [aiErrors, setAiErrors] = useState('');
  const [speechError, setSpeechError] = useState('');
  const [inputType, setInputType] = useState('TEXT');
  const [storedPageType, setStoredPageType] = useState(null);
  const [isInfoPopoverOpen, setIsInfoPopoverOpen] = useState(false);
  const requestStartTime = useRef(null);
  const autoTriggerRef = useRef(false);
  const infoIconAnchorRef = useRef(null);

  const { itemIdsMap, isPILoading, piTriggered, setPiTriggered } = useProductsBatched({
    materials
  });

  const existingMaterials = useMemo(() => {
    return (materials || []).map((material) => {
      return {
        groupId: material.groupId,
        groupName: material.groupName,
        materials: (material.items || []).map((materialItem) => {
          const { product, brand } = getMaterialProduct(materialItem, itemIdsMap);
          return {
            product,
            brand,
            intent: materialItem.intent,
            quantity: materialItem.quantity || 1
          };
        })
      };
    });
  }, [itemIdsMap, materials]);

  const interactingOnAIListBuilder = () => {
    if (!piTriggered) {
      setPiTriggered(true);
    }
  };

  const {
    setGroupedIntentsPreview,
    setScreen,
    screen,
    previousScreen,
    aiPrompt,
    setAiPrompt,
    mcvisId,
    setBathroomQuestionnaire,
    pendingQuestionnaireRequest,
    setPendingQuestionnaireRequest,
    questionnaireError,
    setQuestionnaireError
  } = useContext(AddToContext);

  // Handle speech-to-text transcript changes
  const handleTranscriptChange = useCallback((transcript) => {
    if (transcript) {
      setSpeechError('');
      setInputType('VOICE_TO_TEXT');
      setAiPrompt(transcript);
      interactingOnAIListBuilder();
    }
  }, [setAiPrompt]);

  // Auto-trigger: read prompt from session storage on mount
  useEffect(() => {
    if (!refId || onGenerate) return;
    try {
      const stored = THDStorageUtils.get(SESSION_STORAGE, { key: `lbai_prompt_${refId}` });
      if (stored && stored !== 'undefined') {
        const { prompt, pageType } = JSON.parse(stored);
        if (prompt) {
          THDStorageUtils.remove(SESSION_STORAGE, { key: `lbai_prompt_${refId}` });
          autoTriggerRef.current = true;
          setAiPrompt(prompt);
          setPiTriggered(true);
          if (pageType) setStoredPageType(pageType);
        }
      }
    } catch (err) {
      // Graceful degradation: no auto-trigger, prompt field stays empty
    }
  }, [refId]);

  // useLazyDataModel for AI agent interaction
  const [sendMessage, response] = useLazyDataModel('interactWithAgent', {
    variables: {},
    context: {
      withAuth: true
    }
  });

  const resolvePageType = () => {
    if (storedPageType) return storedPageType;
    const rawPageType = typeof window !== 'undefined'
      ? window.digitalData?.page?.pageInfo?.pageType
      : null;
    return normalizePageType(rawPageType);
  };

  // Fire the second request when the questionnaire sets a pending request
  useEffect(() => {
    if (!pendingQuestionnaireRequest) return;
    if (screen !== SCREENS.AI_LOADING_ANIMATION) return;

    const {
      message,
      templateData,
      inputType: reqInputType,
      refType: reqRefType,
      refId: reqRefId
    } = pendingQuestionnaireRequest;

    requestStartTime.current = Date.now();
    setInputType(reqInputType || 'TEXT');

    sendMessage({
      variables: {
        interactionType: 'MESSAGE',
        agent: 'DEFAULT',
        message,
        templateData,
        metaData: {
          refType: reqRefType,
          refId: reqRefId,
          channel,
          mcvis: mcvisId || '',
          inputType: reqInputType || 'TEXT',
          pageType: resolvePageType()
        }
      }
    });

    setPendingQuestionnaireRequest(null);
  }, [pendingQuestionnaireRequest, screen]);

  const { data, error } = response || {};

  const handleNetworkError = (networkError, pageSection) => {
    // 6.12.2 Post-Page Error Tracking for AI - Network/API errors
    const errorCode = networkError.code || networkError.status || 'n/a';

    // Check if error occurred after 59+ seconds (timeout scenario)
    const timeElapsed = requestStartTime.current ? (Date.now() - requestStartTime.current) : 0;
    const isTimeout = timeElapsed > TIMEOUT;
    const errorMessage = isTimeout
      ? 'We couldn\'t process your list. Try removing a few items and try again.'
      : 'We\'re sorry, something went wrong. Please try again.';

    triggerAnalytics(TRACK.LIST_BUILDER_ERROR, {
      errorType: 'system', // Network/API failures are system errors
      errorMessage: networkError.message || 'Network error occurred',
      errorCode,
      pageSection
    });

    setAiErrors(errorMessage);
    requestStartTime.current = null; // Reset start time
  };

  const handleReturnToErrorScreen = ({ pageSection, networkError, agentError }) => {
    const isFromQuestionnaire = previousScreen === SCREENS.BATHROOM_QUESTIONNAIRE;
    setScreen(isFromQuestionnaire ? SCREENS.BATHROOM_QUESTIONNAIRE : SCREENS.DEFAULT);
    if (isFromQuestionnaire) {
      setQuestionnaireError('We couldn\'t process your added details. Try again or skip this step to continue');
    } else if (networkError) {
      handleNetworkError(networkError, pageSection);
    } else {
      setAiErrors(agentError);
    }
  };

  useEffect(() => {
    // Only process AI responses while on the loading animation screen
    if (screen !== SCREENS.AI_LOADING_ANIMATION) return;

    const isStarterListsOverlay = previousScreen === SCREENS.STARTER_LISTS;
    const pageSection = isStarterListsOverlay ? 'starter lists overlay' : 'add to list overlay';
    const inputTypeForAnalytics = inputType === 'VOICE_TO_TEXT' ? 'speech to text' : 'text';
    const agentErrors = data?.interactWithAgent?.error;

    if (agentErrors) {
      // 6.12.2 Post-Page Error Tracking for AI
      const errorCode = data.interactWithAgent.errorCode || 'n/a';

      triggerAnalytics(TRACK.LIST_BUILDER_ERROR, {
        errorType: 'error', // AI generation failures are business logic errors, not system failures
        errorMessage: data.interactWithAgent.error,
        errorCode,
        pageSection
      });

      handleReturnToErrorScreen({ pageSection, agentError: data.interactWithAgent.error });
    } else if (data) {
      const questions = data?.interactWithAgent?.questions;
      // If response contains questions, route to the questionnaire screen
      if (questions?.length) {
        setBathroomQuestionnaire({
          questions,
          originalPrompt: aiPrompt,
          inputType,
        });
        setScreen(SCREENS.BATHROOM_QUESTIONNAIRE);
        requestStartTime.current = null;
      } else {
        const groupedIntents = formatMaterials(data?.interactWithAgent?.materialsGroups, true, customerType);
        const sessionId = data?.interactWithAgent?.metadata?.sessionId;

        const generatedMaterials = {
          sessionId,
          groupedIntents,
          sourceType: 'ai',
          aiInputMethod: null,
          aiInputType: inputTypeForAnalytics,
          ...(storedPageType && { aiPromptLocation: storedPageType })
        };
        setGroupedIntentsPreview(generatedMaterials);
        setScreen(SCREENS.GENERATED_LIST_INTENTS_PREVIEW);
        requestStartTime.current = null; // Reset start time on success
      }
    }
    if (error) {
      handleReturnToErrorScreen({ pageSection, networkError: error });
    }
  }, [data, error, screen, setGroupedIntentsPreview]);

  // Auto-trigger: fire generation after prompt is set from session storage
  useEffect(() => {
    if (autoTriggerRef.current && aiPrompt.trim()) {
      autoTriggerRef.current = false;
      requestStartTime.current = Date.now();
      sendMessage({
        variables: {
          interactionType: 'MESSAGE',
          agent: 'DEFAULT',
          message: aiPrompt,
          templateData: [
            { key: 'materialsGroup', value: JSON.stringify(existingMaterials || []) },
            { key: 'title', value: name },
            ...(enableLBAIQuestionerAgent ? [{ key: 'bathroomQuestionsEnabled', value: 'true' }] : [])
          ],
          metaData: {
            refType,
            refId,
            channel,
            mcvis: mcvisId || '',
            inputType: 'TEXT',
            pageType: resolvePageType()
          }
        }
      });
      setScreen(SCREENS.AI_LOADING_ANIMATION);
    }
  }, [aiPrompt]);

  const onListeningChange = useCallback((isListening) => {
    if (!isListening) {
      return;
    }

    setInputType('VOICE_TO_TEXT');
  }, []);

  const openInfoPopover = useCallback(() => {
    setIsInfoPopoverOpen(true);
  }, []);

  const closeInfoPopover = useCallback(() => {
    setIsInfoPopoverOpen(false);
  }, []);

  const handleDescriptionChange = (event) => {
    const newText = event.target.value;
    // If the user completely clears the field, reset input type
    if (!newText.trim()) {
      setInputType('TEXT');
    }
    setAiPrompt(newText);
    if (aiErrors || speechError) {
      setAiErrors('');
      setSpeechError('');
    }
    interactingOnAIListBuilder();
  };

  const handleGenerate = () => {
    interactingOnAIListBuilder();
    if (aiPrompt.trim()) {
      if (aiErrors || speechError) {
        setAiErrors('');
        setSpeechError('');
      }

      triggerAnalytics(TRACK.LIST_BUILDER_BUILD_WITH_AI_SELECTION, {
        selection: inputType === 'VOICE_TO_TEXT' ? 'speech to text' : 'text'
      });

      // When onGenerate is provided, delegate to caller instead of calling AI API
      if (onGenerate) {
        onGenerate(aiPrompt);
        return;
      }

      // Track when the request starts for timeout detection
      requestStartTime.current = Date.now();

      // Set the flag to allow the query to fire and trigger the AI agent interaction
      sendMessage({
        variables: {
          interactionType: 'MESSAGE',
          agent: 'DEFAULT',
          message: aiPrompt,
          templateData: [
            { key: 'materialsGroup', value: JSON.stringify(existingMaterials || []) },
            { key: 'title', value: name },
            ...(enableLBAIQuestionerAgent ? [{ key: 'bathroomQuestionsEnabled', value: 'true' }] : [])
          ],
          metaData: {
            refType,
            refId,
            channel,
            mcvis: mcvisId || '',
            inputType,
            pageType: resolvePageType()
          }
        }
      });

      // Navigate to the interstitial loading animation screen
      setScreen(SCREENS.AI_LOADING_ANIMATION);
    }
  };
  const textContent = {
    description: description || 'Describe your project or paste a materials list. '
      + 'We\'ll help you find the right products.',
    infoPopoverContent: (
      <div className="sui-flex sui-flex-col sui-gap-2 sui-w-64">
        <Typography variant="body-base">
          Add things like project type, scope, dimensions and finishes for better results.
        </Typography>
        <div>
          <Typography variant="body-base">Example:</Typography>
          <Typography variant="body-base" color="subtle">
            &quot;5x8 partial bathroom renovation with modern black hardware, large format grey
            tile for the floor, and black hexagon mosaic for the shower&quot;
          </Typography>
        </div>
      </div>
    ),
    placeholder: 'e.g. Building a 10 x 10 deck with composite boards, railing, and concrete footings.',
    speechButtonText: 'Tap to speak'
  };
  const errorMessage = speechError || aiErrors;
  const hasPromptText = Boolean(aiPrompt?.trim());
  const generateVariant = hasPromptText ? 'primary' : 'secondary';
  const showCharCount = (aiPrompt || '').length > 0;

  return (
    <>
      {!hideTitle && (
        <div className="sui-flex sui-items-center sui-justify-center sui-mb-4">
          <img
            src="https://assets.thdstatic.com/images/v1/ai-sparkle-transparent.svg"
            alt="AI Sparkle"
            className="sui-mr-2"
            width="24"
            height="24"
          />
          <div className="sui-mr-2">
            <Typography variant="h3" align="center" weight="display">
              {title}
            </Typography>
          </div>
          <Badge color="info" variant="regular" size="small">
            Beta
          </Badge>
        </div>
      )}
      <Card className="sui-w-full sui-bg-subtle">
        <CardBody>
          {/* Description Text */}
          <div className="sui-mt-2 sui-mb-4 sui-flex sui-gap-2 sui-w-full">
            <Typography variant="body-base" align="center">
              {textContent.description}
            </Typography>
            <div ref={infoIconAnchorRef} className="sui--mt-2 sui-pt-2">
              <InfoFilled
                size="large"
                color="info"
                onMouseEnter={openInfoPopover}
                onMouseLeave={closeInfoPopover}
              />
            </div>
            <Popover
              open={isInfoPopoverOpen}
              anchorEl={infoIconAnchorRef.current}
              placement="top-end"
              onClose={closeInfoPopover}
              onMouseEnter={openInfoPopover}
              onMouseLeave={closeInfoPopover}
            >
              {textContent.infoPopoverContent}
            </Popover>
          </div>

          {/* Text Area with Speech Button */}
          <div className="sui-mb-2">
            <TextField
              placeholder={textContent.placeholder}
              multiline
              minRows={4}
              maxRows={4}
              fullWidth
              value={aiPrompt || ''}
              onChange={handleDescriptionChange}
              InputProps={{
                'aria-label': 'Project description input',
                onFocus: interactingOnAIListBuilder
              }}
            />
            <div className="sui-flex sui-justify-between sui-gap-2 sui-items-start sui-pt-1 sui-min-h-6">
              <div className="sui-flex-1">
                {errorMessage && (
                  <span className="sui-flex sui-items-top sui-gap-1 sui-text-danger">
                    <ErrorFilled color="danger" />
                    <Typography color="danger" variant="body-sm">
                      {errorMessage}
                    </Typography>
                  </span>
                )}
              </div>
              {showCharCount && (
                <Typography
                  variant="body-sm"
                  color="subtle"
                >
                  {aiPrompt.length}
                </Typography>
              )}
            </div>
          </div>

          {/* Generate and Speech Buttons */}
          <div className="sui-flex sui-gap-2 sui-w-full">
            <Button
              data-testid="ai-generate-button"
              variant={generateVariant}
              onClick={handleGenerate}
              disabled={!hasPromptText}
              fullWidth
            >
              Generate
            </Button>
            {!isConsumerApp && (
              <TapToSpeak
                onTranscriptChange={handleTranscriptChange}
                defaultText={textContent.speechButtonText}
                onSpeechErrorChange={setSpeechError}
                onListeningChange={onListeningChange}
              />
            )}
          </div>
        </CardBody>
      </Card>
    </>
  );
};

AIListBuilder.displayName = 'AIListBuilder';

AIListBuilder.propTypes = {
  name: PropTypes.string,
  materials: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  })),
  hideTitle: PropTypes.bool,
  refType: PropTypes.string,
  refId: PropTypes.string,
  onGenerate: PropTypes.func,
  title: PropTypes.string,
  description: PropTypes.string,
};

AIListBuilder.defaultProps = {
  name: '',
  materials: [],
  hideTitle: false,
  refType: '',
  refId: '',
  onGenerate: null,
  title: 'Build your list with AI',
  description: null,
};

AIListBuilder.dataModel = extend({
  interactWithAgent: params({
    agent: customType('Agent').enum(['DEFAULT', 'VERTEX_AI']),
    interactionType: customType('InteractionType').enum(['MESSAGE', 'FEEDBACK']).isRequired(),
    message: string(),
    templateData: arrayOf(
      customType('TemplateData')
        .shape({
          key: string(),
          value: string()
        })
    ),
    metaData: customType('MetadataInput').shape({
      refType: string(),
      refId: string(),
      channel: string(),
      mcvis: string(),
      inputType: string(),
      pageType: string()
    })
  }).mutation().shape({
    metadata: shape({
      inputTokenCount: number(),
      sessionId: string(),
      statusMessage: string()
    }),
    error: string(),
    materialsGroups: arrayOf(
      shape({
        groupName: string(),
        groupId: string(),
        materials: arrayOf(
          shape({
            intent: string(),
            quantity: number(),
            estimateAiQuantity: bool()
          })
        )
      })
    ),
    questions: arrayOf(
      shape({
        id: string(),
        label: string(),
        placeholder: string(),
        helperText: string(),
        prefilledAnswer: string(),
        order: number()
      })
    )
  })
}, useProductsBatched);
